/**
 * /family/:treeSlug/member/:memberId/edit — Rich sectioned Member Editor.
 *
 * A full page (not a modal) with collapsible sections for every field group.
 * Reuses generic Repeater helpers for the list-of-things sections so the
 * code stays compact.
 *
 * Author's note: every field is optional. Sections stay collapsed by default
 * except Basic. The user can populate what they know and come back later.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import SuggestInput from "@/components/SuggestInput";
import {
  ChevronDown, ChevronRight, Save, Trash2, Plus, X, Loader2, ChevronLeft,
  Sparkles, User, Users, Baby, Briefcase, GraduationCap, MapPin, Flag, Church,
  Languages as LangIcon, Heart, Trophy, HandHeart, Images, Video, AudioLines,
  FileText, Flower2, BookOpen, Phone, TreeDeciduous, Star, Link2, Wand2,
} from "lucide-react";

const emptyMember = {
  first_name: "", middle_name: "", last_name: "", nickname: "", other_names: "",
  maiden_name: "", gender: "", current_status: "",
  birth_date: "", birth_date_is_year_only: false,
  death_date: "", death_date_is_year_only: false,
  birth_place: "", death_place: "", burial_location: "", burial_cemetery: "",
  photo: "", cover_photo: "", bio: "",
  birth_star: "",
  alternate_stars: [], parents_extra: {}, spouses: [], children_extra: [],
  occupations: [], education: [], residences: [],
  military: {}, religion: {}, languages: {}, interests: {},
  awards: [], community_service: [],
  photo_albums: [], videos: [], audio: [], documents: [],
  memorial: {}, stories: [], genealogy: {}, contact: {},
  important_events: [], privacy: {}, external_links: [],
};

const ANCESTRY_PROVIDERS = [
  { key: "23andme", label: "23andMe", placeholder: "https://you.23andme.com/…" },
  { key: "ancestry", label: "Ancestry.com", placeholder: "https://www.ancestry.com/family-tree/…" },
  { key: "familysearch", label: "FamilySearch", placeholder: "https://www.familysearch.org/tree/…" },
  { key: "myheritage", label: "MyHeritage", placeholder: "https://www.myheritage.com/…" },
  { key: "gedmatch", label: "GEDmatch", placeholder: "https://app.gedmatch.com/…" },
  { key: "livingdna", label: "LivingDNA", placeholder: "https://www.livingdna.com/…" },
  { key: "geni", label: "Geni", placeholder: "https://www.geni.com/…" },
  { key: "wikitree", label: "WikiTree", placeholder: "https://www.wikitree.com/…" },
  { key: "findmypast", label: "Findmypast", placeholder: "https://www.findmypast.com/…" },
];

export default function MemberEdit() {
  const { treeSlug, memberId } = useParams();
  const nav = useNavigate();
  const [tree, setTree] = useState(null);
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genBio, setGenBio] = useState(false);
  const [genPortrait, setGenPortrait] = useState(false);
  const [portraitStyle, setPortraitStyle] = useState("photorealistic_vintage");
  const [open, setOpen] = useState({ basic: true });

  useEffect(() => {
    (async () => {
      try {
        const { data: t } = await http.get(`/family-trees/${treeSlug}`);
        const treeId = t.tree?.id || t.id;
        setTree({ ...(t.tree || t), id: treeId });
        const { data: prof } = await http.get(`/family-trees/${treeId}/members/${memberId}/profile`);
        setM({ ...emptyMember, ...(prof.member || {}) });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load member");
      } finally { setLoading(false); }
    })();
  }, [treeSlug, memberId]);

  const upd = (k, v) => setM((p) => ({ ...p, [k]: v }));
  const updNested = (k, sub, v) => setM((p) => ({ ...p, [k]: { ...(p[k] || {}), [sub]: v } }));

  const onPhoto = (field) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const max = field === "cover_photo" ? 3 : 2;   // MB raw file; base64 adds ~33%
    if (file.size > max * 1024 * 1024) return toast.error(`${field.replace("_", " ")} must be under ${max} MB — try a smaller image or compress it first.`);
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    upd(field, dataUrl);
  };

  const save = async () => {
    if (!m.first_name?.trim()) return toast.error("First name is required.");
    setSaving(true);
    try {
      await http.patch(`/family-trees/${tree.id}/members/${memberId}`, m);
      toast.success("Saved.");
      nav(`/family/${treeSlug}/member/${memberId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const generatePortrait = async () => {
    if (!tree?.id) return;
    if (m.photo && !window.confirm("This member already has a profile photo. Generate a new AI portrait and save it as an alternate (kept in the portrait gallery, primary photo unchanged)?")) return;
    setGenPortrait(true);
    try {
      const { data } = await http.post(`/family-trees/${tree.id}/members/${memberId}/portrait/generate`, {
        style: portraitStyle,
      });
      if (data?.applied_as_primary && data?.portrait?.url) {
        upd("photo", data.portrait.url);
        toast.success("Portrait generated and set as primary photo.");
      } else {
        toast.success("Portrait generated — view it in the AI Portraits gallery. You can promote it to primary anytime.");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error("Image AI is rate-limited — try again shortly.");
      else toast.error(e?.response?.data?.detail || "Couldn't generate portrait");
    } finally { setGenPortrait(false); }
  };

  const generateBio = async () => {
    if (!tree?.id) return;
    // If the user has an existing bio, ask for confirmation before overwriting.
    const existing = typeof m.bio === "string" ? m.bio : (m.bio && (m.bio[m.bio.__base || "en"] || "")) || "";
    if (existing && existing.trim().length > 40) {
      if (!window.confirm("Replace the current biography with an AI-written version? Your original will be lost.")) return;
    }
    setGenBio(true);
    try {
      const { data } = await http.post(`/family-trees/${tree.id}/members/${memberId}/ai-biography`);
      if (data?.draft) {
        upd("bio", data.draft);
        toast.success("AI biography generated — review & edit as you like, then Save.");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error("AI is rate-limited — try again in a few minutes.");
      else if (status === 503) toast.error("AI biography service is briefly unavailable.");
      else toast.error(e?.response?.data?.detail || "Couldn't generate a biography");
    } finally { setGenBio(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-center opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  if (!m) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="member-edit-page">
      <Link to={`/family/${treeSlug}/member/${memberId}`} className="inline-flex items-center gap-2 text-sm hover:underline mb-3" style={{ color: "var(--wofy-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to profile
      </Link>
      <div className="overline">EDITING MEMBER · {tree?.name}</div>
      <h1 className="font-display text-3xl md:text-4xl mt-1 mb-6">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "New member"}</h1>

      {/* ---------------- Basic ---------------- */}
      <Panel title="Basic Information" icon={User} openState={open} setOpen={setOpen} id="basic">
        <div className="grid grid-cols-3 gap-3">
          <Input label="First name *" value={m.first_name} onChange={(v) => upd("first_name", v)} required testid="me-first" />
          <Input label="Middle name" value={m.middle_name} onChange={(v) => upd("middle_name", v)} testid="me-middle" />
          <Input label="Last name" value={m.last_name} onChange={(v) => upd("last_name", v)} testid="me-last" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Input label="Preferred name / Nickname" value={m.nickname} onChange={(v) => upd("nickname", v)} testid="me-nick" />
          <Input label="Maiden name" value={m.maiden_name} onChange={(v) => upd("maiden_name", v)} testid="me-maiden" />
        </div>
        <Input className="mt-3" label="Other known names / aliases" value={typeof m.other_names === "string" ? m.other_names : ((m.other_names && (m.other_names[m.other_names.__base || "en"] || "")) || "")} onChange={(v) => upd("other_names", v)} testid="me-other" />
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Select label="Gender" value={m.gender} onChange={(v) => upd("gender", v)} options={[["", "—"], ["male", "Male"], ["female", "Female"], ["other", "Other"]]} testid="me-gender" />
          <Select label="Current status" value={m.current_status} onChange={(v) => upd("current_status", v)} options={[["", "—"], ["living", "Living"], ["deceased", "Deceased"], ["unknown", "Unknown"]]} testid="me-status" />
          <div />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Cemetery / Church / Crematorium</Label>
            <SuggestInput
              value={m.burial_cemetery || ""}
              onChange={(v) => upd("burial_cemetery", v)}
              endpoint="/family-trees/burial-suggest"
              params={{ kind: "cemetery" }}
              placeholder="e.g. St Dominic's Church"
              testid="me-burial-cemetery"
            />
          </div>
          <div>
            <Label>Geographical location</Label>
            <SuggestInput
              value={m.burial_location || ""}
              onChange={(v) => upd("burial_location", v)}
              endpoint="/family-trees/burial-suggest"
              params={{ kind: "geo" }}
              placeholder="Village / City, State, Country"
              testid="me-burial-geo"
            />
          </div>
        </div>
        <DateBlock className="mt-3" label="Date of Birth" value={m.birth_date} isYearOnly={m.birth_date_is_year_only} onValue={(v) => upd("birth_date", v)} onYearOnly={(v) => upd("birth_date_is_year_only", v)} testid="me-birth" />
        <Input className="mt-3" label="Place of Birth (Village/City, State, Country)" value={m.birth_place} onChange={(v) => upd("birth_place", v)} testid="me-birthplace" />
        <DateBlock className="mt-3" label="Date of Death (if applicable)" value={m.death_date} isYearOnly={m.death_date_is_year_only} onValue={(v) => upd("death_date", v)} onYearOnly={(v) => upd("death_date_is_year_only", v)} testid="me-death" />
        <Input className="mt-3" label="Place of Death" value={m.death_place} onChange={(v) => upd("death_place", v)} testid="me-deathplace" />
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <Label className="!mb-0">Biography / Life story</Label>
            <button
              type="button"
              onClick={generateBio}
              disabled={genBio || !m.first_name?.trim()}
              className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border transition disabled:opacity-50 hover:shadow-sm"
              style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}
              title="Turn this member's structured data into a warm biographical paragraph"
              data-testid="me-ai-bio"
            >
              {genBio ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              Write with AI
            </button>
          </div>
          <textarea
            dir="auto"
            value={typeof m.bio === "string" ? m.bio : ((m.bio && (m.bio[m.bio.__base || "en"] || "")) || "")}
            onChange={(e) => upd("bio", e.target.value)}
            rows={5}
            className="wofy-input !py-2 !text-sm w-full"
            data-testid="me-bio"
            placeholder="Their story — the AI can seed one from the facts you've entered elsewhere."
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <PhotoField label="Profile photo" value={m.photo} onChange={onPhoto("photo")} onClear={() => upd("photo", "")} preview={m.photo} testid="me-photo" />
          <PhotoField label="Cover photo (banner)" value={m.cover_photo} onChange={onPhoto("cover_photo")} onClear={() => upd("cover_photo", "")} preview={m.cover_photo} wide testid="me-cover" />
        </div>

        {/* ---------- Ancestry Portrait AI ---------- */}
        <div className="mt-4 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #FAF5FF 0%, #FEF3F2 100%)" }}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-800">Ancestry Portrait AI</span>
          </div>
          <div className="text-xs opacity-70 mb-2">
            No photo of this ancestor exists? Generate a period-appropriate portrait using the birth year, gender, and origin already on this record.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={portraitStyle}
              onChange={(e) => setPortraitStyle(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="me-portrait-style"
            >
              <option value="photorealistic_vintage">📷 Photorealistic Vintage</option>
              <option value="painted_oil">🎨 Painted Oil</option>
              <option value="soft_illustrated">🖌️ Soft Illustrated</option>
            </select>
            <button
              type="button"
              onClick={generatePortrait}
              disabled={genPortrait}
              className="wofy-btn-primary text-sm inline-flex items-center gap-2"
              data-testid="me-portrait-generate"
            >
              {genPortrait ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {genPortrait ? "Generating…" : "Generate Portrait"}
            </button>
          </div>
          {(m.ai_portraits || []).length > 0 && (
            <div className="mt-3">
              <div className="text-xs opacity-70 mb-1.5">
                Previously generated ({(m.ai_portraits || []).length}) — click any to promote as primary:
              </div>
              <div className="flex flex-wrap gap-2">
                {(m.ai_portraits || []).slice(-8).reverse().map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={async () => {
                      try {
                        await http.post(`/family-trees/${tree.id}/members/${memberId}/portrait/set-primary`, { portrait_id: p.id });
                        upd("photo", p.url);
                        toast.success("Portrait promoted to primary photo.");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Failed to promote portrait");
                      }
                    }}
                    className="relative group"
                    title={`${p.style} · ${(p.generated_at || "").slice(0,10)}`}
                    data-testid={`me-portrait-thumb-${p.id}`}
                  >
                    <img src={p.url} alt="" className="w-16 h-16 object-cover rounded-md border" style={{ borderColor: "var(--wofy-border)" }} />
                    {p.url === m.photo && (
                      <span className="absolute -top-1 -right-1 bg-green-600 text-white text-[9px] px-1 rounded">PRIMARY</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Panel>

      {/* ---------------- Astrology / Star signs ---------------- */}
      <Panel title="Birth Stars & Astrology" icon={Star} openState={open} setOpen={setOpen} id="stars">
        <Input label="Birth Star / Western Zodiac (Libra, Virgo, Leo …)" value={m.birth_star} onChange={(v) => upd("birth_star", v)} testid="me-birthstar" />
        <div className="mt-3">
          <Label>Alternate Star Systems (nakshatra, Chinese zodiac, Celtic, Mayan, custom …)</Label>
          <Repeater
            items={m.alternate_stars || []}
            onChange={(v) => upd("alternate_stars", v)}
            addLabel="Add another star sign"
            blank={{ system: "", name: "", note: "" }}
            testid="stars"
            render={(item, upItem) => (
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="System (e.g. nakshatra, chinese)" value={item.system} onChange={(v) => upItem({ ...item, system: v })} />
                <Input placeholder="Name (e.g. Rohini, Dragon)" value={item.name} onChange={(v) => upItem({ ...item, name: v })} />
                <Input placeholder="Note (optional)" value={item.note} onChange={(v) => upItem({ ...item, note: v })} />
              </div>
            )}
          />
        </div>
      </Panel>

      {/* ---------------- Parents (extras) ---------------- */}
      <Panel title="Parents & Guardians" icon={Users} openState={open} setOpen={setOpen} id="parents">
        <p className="text-xs opacity-70 mb-2">Structured father/mother links live on the tree graph. Use these fields for step / adoptive / foster parents & guardians not yet added as members.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Step Father" value={m.parents_extra?.step_father || ""} onChange={(v) => updNested("parents_extra", "step_father", v)} />
          <Input label="Step Mother" value={m.parents_extra?.step_mother || ""} onChange={(v) => updNested("parents_extra", "step_mother", v)} />
          <Input label="Adoptive Father" value={m.parents_extra?.adoptive_father || ""} onChange={(v) => updNested("parents_extra", "adoptive_father", v)} />
          <Input label="Adoptive Mother" value={m.parents_extra?.adoptive_mother || ""} onChange={(v) => updNested("parents_extra", "adoptive_mother", v)} />
        </div>
        <Input className="mt-3" label="Foster Parents (comma separated)" value={(m.parents_extra?.foster_parents || []).join(", ")} onChange={(v) => updNested("parents_extra", "foster_parents", v.split(",").map((x) => x.trim()).filter(Boolean))} />
        <Input className="mt-3" label="Guardians (comma separated)" value={(m.parents_extra?.guardians || []).join(", ")} onChange={(v) => updNested("parents_extra", "guardians", v.split(",").map((x) => x.trim()).filter(Boolean))} />
      </Panel>

      {/* ---------------- Spouses ---------------- */}
      <Panel title="Spouses" icon={Heart} openState={open} setOpen={setOpen} id="spouses">
        <Repeater
          items={m.spouses || []}
          onChange={(v) => upd("spouses", v)}
          addLabel="Add a spouse"
          blank={{ name: "", marriage_date: "", marriage_location: "", separation_date: "", divorce_date: "", widowed_date: "", status: "" }}
          testid="spouses"
          render={(s, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Name" value={s.name} onChange={(v) => up({ ...s, name: v })} />
                <Select label="Status" value={s.status} onChange={(v) => up({ ...s, status: v })} options={[["", "—"], ["current", "Currently married"], ["separated", "Separated"], ["divorced", "Divorced"], ["widowed", "Widowed"]]} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="date" label="Marriage date" value={s.marriage_date} onChange={(v) => up({ ...s, marriage_date: v })} />
                <Input label="Marriage location" value={s.marriage_location} onChange={(v) => up({ ...s, marriage_location: v })} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input type="date" label="Separation date" value={s.separation_date} onChange={(v) => up({ ...s, separation_date: v })} />
                <Input type="date" label="Divorce date" value={s.divorce_date} onChange={(v) => up({ ...s, divorce_date: v })} />
                <Input type="date" label="Widowed date" value={s.widowed_date} onChange={(v) => up({ ...s, widowed_date: v })} />
              </div>
            </>
          )}
        />
      </Panel>

      {/* ---------------- Children (extras) ---------------- */}
      <Panel title="Children" icon={Baby} openState={open} setOpen={setOpen} id="children">
        <p className="text-xs opacity-70 mb-2">Linked children are auto-counted from the tree graph. Use this list for children not yet added as members.</p>
        <Repeater
          items={m.children_extra || []}
          onChange={(v) => upd("children_extra", v)}
          addLabel="Add a child"
          blank={{ name: "", birth_date: "", gender: "", adopted: false, foster: false, step: false }}
          testid="children"
          render={(c, up) => (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Input label="Name" value={c.name} onChange={(v) => up({ ...c, name: v })} />
                <Input type="date" label="Birth date" value={c.birth_date} onChange={(v) => up({ ...c, birth_date: v })} />
                <Select label="Gender" value={c.gender} onChange={(v) => up({ ...c, gender: v })} options={[["", "—"], ["male", "Male"], ["female", "Female"], ["other", "Other"]]} />
              </div>
              <div className="mt-2 flex gap-4 text-xs">
                <Checkbox label="Adopted" checked={c.adopted} onChange={(v) => up({ ...c, adopted: v })} />
                <Checkbox label="Foster" checked={c.foster} onChange={(v) => up({ ...c, foster: v })} />
                <Checkbox label="Step child" checked={c.step} onChange={(v) => up({ ...c, step: v })} />
              </div>
            </>
          )}
        />
      </Panel>

      {/* ---------------- Career ---------------- */}
      <Panel title="Career / Occupations" icon={Briefcase} openState={open} setOpen={setOpen} id="career">
        <Repeater
          items={m.occupations || []}
          onChange={(v) => upd("occupations", v)}
          addLabel="Add an occupation"
          blank={{ title: "", company: "", industry: "", years_active: "", retirement_year: "", notes: "" }}
          testid="occupations"
          render={(o, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Title / role" value={o.title} onChange={(v) => up({ ...o, title: v })} />
                <Input label="Company / business" value={o.company} onChange={(v) => up({ ...o, company: v })} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input label="Industry" value={o.industry} onChange={(v) => up({ ...o, industry: v })} />
                <Input label="Years active (e.g. 1998-2015)" value={o.years_active} onChange={(v) => up({ ...o, years_active: v })} />
                <Input label="Retirement year" value={o.retirement_year} onChange={(v) => up({ ...o, retirement_year: v })} />
              </div>
              <Textarea className="mt-2" label="Notes" value={o.notes} onChange={(v) => up({ ...o, notes: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Education ---------------- */}
      <Panel title="Education" icon={GraduationCap} openState={open} setOpen={setOpen} id="edu">
        <Repeater
          items={m.education || []}
          onChange={(v) => upd("education", v)}
          addLabel="Add an education entry"
          blank={{ level: "", name: "", degree: "", area: "", graduation_year: "", notes: "" }}
          testid="edu"
          render={(e, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Select label="Level" value={e.level} onChange={(v) => up({ ...e, level: v })} options={[["", "—"], ["school", "School"], ["college", "College"], ["university", "University"], ["certification", "Certification"]]} />
                <Input label="Name of institution" value={e.name} onChange={(v) => up({ ...e, name: v })} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input label="Degree" value={e.degree} onChange={(v) => up({ ...e, degree: v })} />
                <Input label="Area / subject" value={e.area} onChange={(v) => up({ ...e, area: v })} />
                <Input label="Graduation year" value={e.graduation_year} onChange={(v) => up({ ...e, graduation_year: v })} />
              </div>
              <Textarea className="mt-2" label="Notes" value={e.notes} onChange={(v) => up({ ...e, notes: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Contact ---------------- */}
      <Panel title="Contact (living only)" icon={Phone} openState={open} setOpen={setOpen} id="contact">
        <div className="grid grid-cols-3 gap-2">
          <Input label="Current city" value={m.contact?.current_city || ""} onChange={(v) => updNested("contact", "current_city", v)} />
          <Input label="State" value={m.contact?.state || ""} onChange={(v) => updNested("contact", "state", v)} />
          <Input label="Country" value={m.contact?.country || ""} onChange={(v) => updNested("contact", "country", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input label="Phone" value={m.contact?.phone || ""} onChange={(v) => updNested("contact", "phone", v)} />
          <Input label="Email" value={m.contact?.email || ""} onChange={(v) => updNested("contact", "email", v)} />
        </div>
        <Input className="mt-2" label="Website" value={m.contact?.website || ""} onChange={(v) => updNested("contact", "website", v)} />
        <div className="grid grid-cols-2 gap-2 mt-2">
          {["linkedin", "twitter", "instagram", "facebook", "youtube", "tiktok"].map((k) => (
            <Input key={k} label={k[0].toUpperCase() + k.slice(1)} value={(m.contact?.social || {})[k] || ""} onChange={(v) => updNested("contact", "social", { ...(m.contact?.social || {}), [k]: v })} />
          ))}
        </div>
      </Panel>

      {/* ---------------- Residences ---------------- */}
      <Panel title="Residence History" icon={MapPin} openState={open} setOpen={setOpen} id="residences">
        <Repeater
          items={m.residences || []}
          onChange={(v) => upd("residences", v)}
          addLabel="Add a residence"
          blank={{ kind: "lived", country: "", state: "", city: "", from_date: "", to_date: "", notes: "" }}
          testid="residences"
          render={(r, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Select label="Kind" value={r.kind} onChange={(v) => up({ ...r, kind: v })} options={[["childhood", "Childhood home"], ["family", "Family home"], ["lived", "Lived here"]]} />
                <Input label="City" value={r.city} onChange={(v) => up({ ...r, city: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input label="State / Region" value={r.state} onChange={(v) => up({ ...r, state: v })} />
                <Input label="Country" value={r.country} onChange={(v) => up({ ...r, country: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="date" label="From" value={r.from_date} onChange={(v) => up({ ...r, from_date: v })} />
                <Input type="date" label="To (leave empty for present)" value={r.to_date} onChange={(v) => up({ ...r, to_date: v })} />
              </div>
            </>
          )}
        />
      </Panel>

      {/* ---------------- Important Events ---------------- */}
      <Panel title="Important Dates & Events" icon={Sparkles} openState={open} setOpen={setOpen} id="events">
        <Repeater
          items={m.important_events || []}
          onChange={(v) => upd("important_events", v)}
          addLabel="Add an event"
          blank={{ type: "", title: "", date: "", location: "", description: "" }}
          testid="events"
          render={(ev, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Select label="Type" value={ev.type} onChange={(v) => up({ ...ev, type: v })} options={[["", "—"], ["birth", "Birth"], ["baptism", "Baptism"], ["communion", "Communion"], ["confirmation", "Confirmation"], ["graduation", "Graduation"], ["wedding", "Wedding"], ["military", "Military service"], ["immigration", "Immigration"], ["citizenship", "Citizenship"], ["business", "Business started"], ["retirement", "Retirement"], ["award", "Award"], ["anniversary", "Anniversary"], ["death", "Death"], ["other", "Other"]]} />
                <Input label="Title" value={ev.title} onChange={(v) => up({ ...ev, title: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="date" label="Date" value={ev.date} onChange={(v) => up({ ...ev, date: v })} />
                <Input label="Location" value={ev.location} onChange={(v) => up({ ...ev, location: v })} />
              </div>
              <Textarea className="mt-2" label="Description" value={ev.description} onChange={(v) => up({ ...ev, description: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Military ---------------- */}
      <Panel title="Military Service" icon={Flag} openState={open} setOpen={setOpen} id="military">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Branch" value={m.military?.branch || ""} onChange={(v) => updNested("military", "branch", v)} />
          <Input label="Rank" value={m.military?.rank || ""} onChange={(v) => updNested("military", "rank", v)} />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Input label="Service number" value={m.military?.service_number || ""} onChange={(v) => updNested("military", "service_number", v)} />
          <Input label="Years served (e.g. 1965-1972)" value={m.military?.years_served || ""} onChange={(v) => updNested("military", "years_served", v)} />
          <Input label="Veteran status" value={m.military?.veteran_status || ""} onChange={(v) => updNested("military", "veteran_status", v)} />
        </div>
        <Input className="mt-2" label="Wars (comma separated)" value={(m.military?.wars || []).join(", ")} onChange={(v) => updNested("military", "wars", v.split(",").map((x) => x.trim()).filter(Boolean))} />
        <Input className="mt-2" label="Medals (comma separated)" value={(m.military?.medals || []).join(", ")} onChange={(v) => updNested("military", "medals", v.split(",").map((x) => x.trim()).filter(Boolean))} />
        <Textarea className="mt-2" label="Notes" value={m.military?.notes || ""} onChange={(v) => updNested("military", "notes", v)} rows={2} />
      </Panel>

      {/* ---------------- Religion ---------------- */}
      <Panel title="Religion" icon={Church} openState={open} setOpen={setOpen} id="religion">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Faith" value={m.religion?.faith || ""} onChange={(v) => updNested("religion", "faith", v)} />
          <Input label="Parish" value={m.religion?.parish || ""} onChange={(v) => updNested("religion", "parish", v)} />
        </div>
        <Input className="mt-2" label="Place of worship (Church/Temple/Mosque)" value={m.religion?.place_of_worship || ""} onChange={(v) => updNested("religion", "place_of_worship", v)} />
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Input type="date" label="Baptism date" value={m.religion?.baptism_date || ""} onChange={(v) => updNested("religion", "baptism_date", v)} />
          <Input type="date" label="Confirmation date" value={m.religion?.confirmation_date || ""} onChange={(v) => updNested("religion", "confirmation_date", v)} />
          <Input type="date" label="Ordination date" value={m.religion?.ordination_date || ""} onChange={(v) => updNested("religion", "ordination_date", v)} />
        </div>
      </Panel>

      {/* ---------------- Languages ---------------- */}
      <Panel title="Languages" icon={LangIcon} openState={open} setOpen={setOpen} id="languages">
        <Input label="Native (comma separated)" value={(m.languages?.native || []).join(", ")} onChange={(v) => updNested("languages", "native", v.split(",").map((x) => x.trim()).filter(Boolean))} />
        <Input className="mt-2" label="Other languages (comma separated)" value={(m.languages?.other || []).join(", ")} onChange={(v) => updNested("languages", "other", v.split(",").map((x) => x.trim()).filter(Boolean))} />
      </Panel>

      {/* ---------------- Interests ---------------- */}
      <Panel title="Personal Interests" icon={Heart} openState={open} setOpen={setOpen} id="interests">
        {["hobbies", "sports", "music", "books", "movies", "travel", "cooking", "gardening", "community"].map((k) => (
          <Input key={k} className="mb-2" label={k[0].toUpperCase() + k.slice(1) + " (comma separated)"} value={(m.interests?.[k] || []).join(", ")} onChange={(v) => updNested("interests", k, v.split(",").map((x) => x.trim()).filter(Boolean))} />
        ))}
      </Panel>

      {/* ---------------- Awards ---------------- */}
      <Panel title="Awards & Achievements" icon={Trophy} openState={open} setOpen={setOpen} id="awards">
        <Repeater
          items={m.awards || []}
          onChange={(v) => upd("awards", v)}
          addLabel="Add an award"
          blank={{ award: "", organization: "", date: "", description: "" }}
          testid="awards"
          render={(a, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Award" value={a.award} onChange={(v) => up({ ...a, award: v })} />
                <Input label="Organization" value={a.organization} onChange={(v) => up({ ...a, organization: v })} />
              </div>
              <Input type="date" className="mt-2" label="Date" value={a.date} onChange={(v) => up({ ...a, date: v })} />
              <Textarea className="mt-2" label="Description" value={a.description} onChange={(v) => up({ ...a, description: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Community Service ---------------- */}
      <Panel title="Community Service" icon={HandHeart} openState={open} setOpen={setOpen} id="community">
        <Repeater
          items={m.community_service || []}
          onChange={(v) => upd("community_service", v)}
          addLabel="Add community service entry"
          blank={{ type: "volunteer", org: "", role: "", from_date: "", to_date: "", description: "" }}
          testid="community"
          render={(c, up) => (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Select label="Type" value={c.type} onChange={(v) => up({ ...c, type: v })} options={[["volunteer", "Volunteer"], ["charity", "Charity"], ["leadership", "Leadership"], ["religious", "Religious"]]} />
                <Input label="Organization" value={c.org} onChange={(v) => up({ ...c, org: v })} />
                <Input label="Role" value={c.role} onChange={(v) => up({ ...c, role: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="date" label="From" value={c.from_date} onChange={(v) => up({ ...c, from_date: v })} />
                <Input type="date" label="To" value={c.to_date} onChange={(v) => up({ ...c, to_date: v })} />
              </div>
              <Textarea className="mt-2" label="Description" value={c.description} onChange={(v) => up({ ...c, description: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Media — Photo Albums ---------------- */}
      <Panel title="Photo Albums" icon={Images} openState={open} setOpen={setOpen} id="albums">
        <Repeater
          items={m.photo_albums || []}
          onChange={(v) => upd("photo_albums", v)}
          addLabel="Add an album"
          blank={{ name: "", photos: [""] }}
          testid="albums"
          render={(alb, up) => (
            <>
              <Select label="Album" value={alb.name} onChange={(v) => up({ ...alb, name: v })} options={[
                ["childhood", "Childhood"], ["wedding", "Wedding"], ["family", "Family"],
                ["career", "Career"], ["vacation", "Vacations"], ["celebrations", "Celebrations"],
                ["military", "Military"], ["school", "School"], ["friends", "Friends"], ["custom", "Custom"],
              ]} />
              <div className="mt-2 space-y-1">
                {(alb.photos || []).map((p, pi) => (
                  <div key={pi} className="flex gap-2">
                    <input
                      className="wofy-input flex-1 !py-1.5 !text-sm"
                      placeholder="Photo URL (paste from your storage or upload service)"
                      value={p}
                      onChange={(e) => up({ ...alb, photos: alb.photos.map((x, i) => i === pi ? e.target.value : x) })}
                    />
                    <button type="button" onClick={() => up({ ...alb, photos: alb.photos.filter((_, i) => i !== pi) })} className="opacity-60 hover:opacity-100">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => up({ ...alb, photos: [...(alb.photos || []), ""] })} className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100">
                  <Plus className="w-3 h-3" /> Add another photo URL
                </button>
              </div>
            </>
          )}
        />
      </Panel>

      {/* ---------------- Videos ---------------- */}
      <Panel title="Videos" icon={Video} openState={open} setOpen={setOpen} id="videos">
        <Repeater
          items={m.videos || []}
          onChange={(v) => upd("videos", v)}
          addLabel="Add a video"
          blank={{ title: "", kind: "family", url: "", thumbnail: "", note: "" }}
          testid="videos"
          render={(v, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Title" value={v.title} onChange={(x) => up({ ...v, title: x })} />
                <Select label="Kind" value={v.kind} onChange={(x) => up({ ...v, kind: x })} options={[["family", "Family video"], ["interview", "Interview"], ["memorial", "Memorial video"], ["wedding", "Wedding video"], ["other", "Other"]]} />
              </div>
              <Input className="mt-2" label="Video URL (YouTube, Vimeo, direct link)" value={v.url} onChange={(x) => up({ ...v, url: x })} />
              <Input className="mt-2" label="Thumbnail URL (optional)" value={v.thumbnail} onChange={(x) => up({ ...v, thumbnail: x })} />
              <Textarea className="mt-2" label="Note" value={v.note} onChange={(x) => up({ ...v, note: x })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Audio ---------------- */}
      <Panel title="Audio Recordings" icon={AudioLines} openState={open} setOpen={setOpen} id="audio">
        <Repeater
          items={m.audio || []}
          onChange={(v) => upd("audio", v)}
          addLabel="Add an audio recording"
          blank={{ title: "", kind: "voice", url: "", note: "" }}
          testid="audio"
          render={(a, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Title" value={a.title} onChange={(x) => up({ ...a, title: x })} />
                <Select label="Kind" value={a.kind} onChange={(x) => up({ ...a, kind: x })} options={[["voice", "Voice recording"], ["interview", "Interview"], ["song", "Song"], ["speech", "Speech"], ["other", "Other"]]} />
              </div>
              <Input className="mt-2" label="Audio URL (mp3/wav)" value={a.url} onChange={(x) => up({ ...a, url: x })} />
              <Textarea className="mt-2" label="Note" value={a.note} onChange={(x) => up({ ...a, note: x })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Documents ---------------- */}
      <Panel title="Documents" icon={FileText} openState={open} setOpen={setOpen} id="documents">
        <Repeater
          items={m.documents || []}
          onChange={(v) => upd("documents", v)}
          addLabel="Add a document"
          blank={{ kind: "other", name: "", url: "", note: "" }}
          testid="documents"
          render={(d, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Select label="Kind" value={d.kind} onChange={(v) => up({ ...d, kind: v })} options={[
                  ["birth_cert", "Birth Certificate"], ["marriage_cert", "Marriage Certificate"],
                  ["death_cert", "Death Certificate"], ["passport", "Passport"],
                  ["immigration", "Immigration Papers"], ["degree", "Degree / Diploma"],
                  ["newspaper", "Newspaper Article"], ["letter", "Family Letter"],
                  ["diary", "Diary"], ["recipe", "Recipe"], ["bible", "Family Bible Page"], ["other", "Other"],
                ]} />
                <Input label="Document name" value={d.name} onChange={(v) => up({ ...d, name: v })} />
              </div>
              <Input className="mt-2" label="URL (link to file or scan)" value={d.url} onChange={(v) => up({ ...d, url: v })} />
              <Textarea className="mt-2" label="Note" value={d.note} onChange={(v) => up({ ...d, note: v })} rows={2} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Memorial ---------------- */}
      <Panel title="Memorial Information" icon={Flower2} openState={open} setOpen={setOpen} id="memorial">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Cemetery" value={m.memorial?.cemetery || ""} onChange={(v) => updNested("memorial", "cemetery", v)} />
          <Input label="Gravesite" value={m.memorial?.gravesite || ""} onChange={(v) => updNested("memorial", "gravesite", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input label="GPS latitude" value={m.memorial?.gps_lat || ""} onChange={(v) => updNested("memorial", "gps_lat", v)} />
          <Input label="GPS longitude" value={m.memorial?.gps_lng || ""} onChange={(v) => updNested("memorial", "gps_lng", v)} />
        </div>
        <Textarea className="mt-2" label="Obituary" value={m.memorial?.obituary || ""} onChange={(v) => updNested("memorial", "obituary", v)} rows={4} />
        <div className="mt-2 flex items-center gap-2">
          <Checkbox label="Send anniversary reminders to family" checked={!!m.memorial?.anniversary_reminder} onChange={(v) => updNested("memorial", "anniversary_reminder", v)} />
        </div>
      </Panel>

      {/* ---------------- Stories ---------------- */}
      <Panel title="Family Stories" icon={BookOpen} openState={open} setOpen={setOpen} id="stories">
        <Repeater
          items={m.stories || []}
          onChange={(v) => upd("stories", v)}
          addLabel="Add a story"
          blank={{ title: "", kind: "childhood", body: "" }}
          testid="stories"
          render={(s, up) => (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Title" value={s.title} onChange={(v) => up({ ...s, title: v })} />
                <Select label="Kind" value={s.kind} onChange={(v) => up({ ...s, kind: v })} options={[
                  ["childhood", "Childhood story"], ["funny", "Funny story"], ["lesson", "Life lesson"],
                  ["tradition", "Family tradition"], ["recipe", "Recipe"], ["quote", "Quote"],
                  ["expression", "Favorite expression"], ["other", "Other"],
                ]} />
              </div>
              <Textarea className="mt-2" label="Story" value={s.body} onChange={(v) => up({ ...s, body: v })} rows={5} />
            </>
          )}
        />
      </Panel>

      {/* ---------------- Genealogy + External DNA/Ancestry Links ---------------- */}
      <Panel title="Genealogy & External Ancestry Links" icon={TreeDeciduous} openState={open} setOpen={setOpen} id="genealogy">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Family branch" value={m.genealogy?.branch || ""} onChange={(v) => updNested("genealogy", "branch", v)} />
          <Input label="Clan" value={m.genealogy?.clan || ""} onChange={(v) => updNested("genealogy", "clan", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input label="House name" value={m.genealogy?.house_name || ""} onChange={(v) => updNested("genealogy", "house_name", v)} />
          <Input label="Surname origin" value={m.genealogy?.surname_origin || ""} onChange={(v) => updNested("genealogy", "surname_origin", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input label="Ethnicity (optional)" value={m.genealogy?.ethnicity || ""} onChange={(v) => updNested("genealogy", "ethnicity", v)} />
          <Input label="Family motto" value={m.genealogy?.family_motto || ""} onChange={(v) => updNested("genealogy", "family_motto", v)} />
        </div>
        <Input className="mt-2" label="Coat of arms URL (image link)" value={m.genealogy?.coat_of_arms_url || ""} onChange={(v) => updNested("genealogy", "coat_of_arms_url", v)} />

        <div className="mt-4 p-3 rounded-lg border" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-3.5 h-3.5" />
            <Label className="!m-0">External Ancestry / DNA Profiles</Label>
          </div>
          <p className="text-xs opacity-70 mb-3">Link this member's profile on external genealogy sites. Only add public/shareable links.</p>
          <div className="grid grid-cols-1 gap-2">
            {ANCESTRY_PROVIDERS.map((p) => {
              // Store as structured objects inside genealogy.dna_test_links
              // (keyed by provider). Backend field is a free-form list; we
              // serialise to [{provider, url}, ...] on save.
              const val = (m.genealogy?.external_links || []).find((l) => l.provider === p.key)?.url || "";
              const setVal = (u) => {
                const links = (m.genealogy?.external_links || []).filter((l) => l.provider !== p.key);
                if (u.trim()) links.push({ provider: p.key, label: p.label, url: u.trim() });
                updNested("genealogy", "external_links", links);
              };
              return (
                <div key={p.key} className="flex gap-2 items-center">
                  <div className="min-w-[130px] text-xs font-medium opacity-80">{p.label}</div>
                  <input
                    className="wofy-input flex-1 !py-1.5 !text-sm"
                    placeholder={p.placeholder}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    data-testid={`me-ancestry-${p.key}`}
                  />
                </div>
              );
            })}
          </div>

          <Label className="mt-4">Other DNA test links (comma separated URLs)</Label>
          <input
            className="wofy-input !py-1.5 !text-sm"
            placeholder="https://…, https://…"
            value={(m.genealogy?.dna_test_links || []).join(", ")}
            onChange={(e) => updNested("genealogy", "dna_test_links", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
        </div>
      </Panel>

      {/* ---------------- Actions ---------------- */}
      <div className="wofy-card !p-4 mt-8 flex items-center justify-end gap-2 sticky bottom-4">
        <Link to={`/family/${treeSlug}/member/${memberId}`} className="wofy-btn-ghost text-sm">Cancel</Link>
        <button onClick={save} disabled={saving} className="wofy-btn-primary text-sm" data-testid="me-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save all changes
        </button>
      </div>
    </div>
  );
}

// ========== reusable UI ==========

function Panel({ title, icon: Icon, id, openState, setOpen, children }) {
  const isOpen = openState[id];
  return (
    <section className="wofy-card !p-0 mb-3 overflow-hidden" data-testid={`me-panel-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((p) => ({ ...p, [id]: !p[id] }))}
        className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-black/[0.02]"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {Icon && <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />}
        <span className="font-display text-lg">{title}</span>
      </button>
      {isOpen && <div className="px-4 pb-4 pt-1">{children}</div>}
    </section>
  );
}

function Label({ children, className = "" }) {
  return <label className={`block text-xs font-mono uppercase tracking-wider mb-1 opacity-70 ${className}`}>{children}</label>;
}

function Input({ label, value, onChange, type = "text", required, placeholder, className = "", testid }) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <input
        type={type}
        dir="auto"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        required={required}
        className="wofy-input !py-1.5 !text-sm w-full"
        data-testid={testid}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, className = "", testid }) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <textarea
        dir="auto"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="wofy-input !py-2 !text-sm w-full"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, className = "", testid }) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="wofy-input !py-1.5 !text-sm w-full" data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function PhotoField({ label, onChange, onClear, preview, wide, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type="file" accept="image/*" onChange={onChange} className="text-xs" data-testid={testid} />
      {preview && (
        <div className="mt-2 flex items-start gap-2">
          <img src={preview} alt="" className={wide ? "w-40 h-16 object-cover rounded" : "w-16 h-16 object-cover rounded-full"} />
          <button type="button" onClick={onClear} className="text-xs opacity-60 hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function DateBlock({ label, value, isYearOnly, onValue, onYearOnly, className = "", testid }) {
  // The "date" toggle button was previously broken: clicking it only cleared
  // `isYearOnly`, but the derived mode is `isYearOnly ? "year" : value ? "date" : "unknown"`.
  // With no value yet, mode collapsed back to "unknown" and the date picker
  // never appeared. Track the user's *intent* locally so the toggle sticks
  // even before they've entered anything.
  const [wantsDate, setWantsDate] = useState(!isYearOnly && !!value);
  useEffect(() => {
    // Sync from external prop changes (e.g. loading a different member).
    setWantsDate(!isYearOnly && !!value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isYearOnly]);
  const mode = isYearOnly ? "year" : (value || wantsDate) ? "date" : "unknown";
  const setMode = (m) => {
    if (m === "unknown") { onValue(""); onYearOnly(false); setWantsDate(false); }
    else if (m === "year") { onYearOnly(true); setWantsDate(false); }
    else { onYearOnly(false); setWantsDate(true); }
  };
  return (
    <div className={className}>
      <Label>{label}</Label>
      <div className="flex gap-1 mb-2">
        {[["date", "Full date"], ["year", "Year only"], ["unknown", "Unknown"]].map(([v, l]) => (
          <button type="button" key={v} onClick={() => setMode(v)} data-testid={testid && `${testid}-mode-${v}`} className="text-[10px] font-mono uppercase px-2 py-1 rounded-full border" style={{
            borderColor: mode === v ? "var(--wofy-primary)" : "var(--wofy-border)",
            background: mode === v ? "var(--wofy-primary)" : "transparent",
            color: mode === v ? "white" : "var(--wofy-text-soft)",
          }}>{l}</button>
        ))}
      </div>
      {mode === "date" && <input type="date" value={value || ""} onChange={(e) => onValue(e.target.value)} className="wofy-input !py-1.5 !text-sm w-full" data-testid={testid && `${testid}-date`} />}
      {mode === "year" && <input type="text" value={value || ""} maxLength={4} onChange={(e) => onValue(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 1952" className="wofy-input !py-1.5 !text-sm w-full" data-testid={testid && `${testid}-year`} />}
      {mode === "unknown" && <div className="text-xs italic opacity-60">Recorded as unknown.</div>}
    </div>
  );
}

function Repeater({ items, onChange, blank, addLabel, render, testid }) {
  const add = () => onChange([...(items || []), { ...blank }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updItem = (i, v) => onChange(items.map((x, idx) => idx === i ? v : x));
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={i} className="border rounded-lg p-3 relative" style={{ borderColor: "var(--wofy-border)" }} data-testid={testid && `${testid}-${i}`}>
          <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 opacity-60 hover:opacity-100" aria-label="Remove">
            <X className="w-4 h-4" />
          </button>
          {render(it, (v) => updItem(i, v))}
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid={testid && `${testid}-add`}>
        <Plus className="w-3 h-3" /> {addLabel || "Add"}
      </button>
    </div>
  );
}
